import { useAuth, useInboxConversation } from "@tellescope/tanstack-query";
import { ReversePaginatedList, Spinner } from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { EmailMessage } from "./EmailMessage";
import { useInboxThreadSearchParams } from "../../hooks";
import { useMemo } from "react";

export interface InboxConversationEmailListProps {
  threadId: string;
  conversationData?: ReturnType<typeof useInboxConversation>;
}

export const InboxConversationEmailList = ({ threadId, conversationData }: InboxConversationEmailListProps) => {
  const { t } = useTranslation("inbox");
  const { currentUser } = useAuth();

  const internalConversationData = useInboxConversation(threadId, { enabled: !conversationData });
  const { metadata, messages, scheduled, loadNextPage, hasNextPage, isLoading, markReadOrUnread } =
    conversationData ?? internalConversationData;
  const [queryParams] = useInboxThreadSearchParams();
  const draftId = queryParams.draftId;

  const allMessages = useMemo(() => [...messages, ...scheduled], [messages, scheduled]);
  const messagesToDisplay = useMemo(() => {
    if (!draftId) return allMessages;

    return allMessages.filter(({ id }) => id !== draftId);
  }, [allMessages, draftId]);

  const firstForwardedMessageId = useMemo(() => {
    const forwarded = messagesToDisplay.filter(({ content }) => {
      const withoutSoftBreaks = content.replace(/=\r?\n/g, "");
      return withoutSoftBreaks.includes("tellescope-forwarded") || withoutSoftBreaks.includes("gmail_quote_container");
    });
    if (forwarded.length === 0) return undefined;
    return forwarded.reduce((earliest, current) =>
      current.timestamp.getTime() < earliest.timestamp.getTime() ? current : earliest
    ).id;
  }, [messagesToDisplay]);

  const isUnread = currentUser.id ? !metadata?.readBy?.[currentUser.id] : false;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner />
      </div>
    );
  }

  if (!metadata) {
    <div className="flex items-center justify-center h-full">
      <p className="text-destructive">{t("There was an error loading this thread...")}</p>
    </div>;
  }

  if (allMessages.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center p-8 text-center">
        <div className="text-sm text-muted-foreground">{t("No emails in this thread yet")}</div>
      </div>
    );
  }

  return (
    <ReversePaginatedList
      className="flex-1 overflow-y-auto pb-4 space-y-4"
      onTopReached={() => {
        if (hasNextPage) {
          loadNextPage();
        }
      }}
      onBottomReached={() => {
        if (isUnread && !!currentUser) {
          markReadOrUnread({ userId: currentUser.id, readStatus: "read" });
        }
      }}
      onFocusWithin={({ isAtBottom }) => {
        if (isAtBottom && isUnread && currentUser) {
          markReadOrUnread({ userId: currentUser.id, readStatus: "read" });
        }
      }}
      onClickWithin={({ isAtBottom }) => {
        if (isAtBottom && isUnread && currentUser) {
          markReadOrUnread({ userId: currentUser.id, readStatus: "read" });
        }
      }}
    >
      {messagesToDisplay.map((message) => (
        <EmailMessage
          key={message.id}
          message={message}
          className="px-4"
          renderFullContent={message.id === firstForwardedMessageId}
        />
      ))}
    </ReversePaginatedList>
  );
};
